import { useEffect, useCallback, useState, createContext, useContext } from 'react'
import { Loader2, ArrowLeft, RotateCcw } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useIsAdmin } from '@/hooks/useAdmin'
import { useDiscovery, type UseDiscoveryReturn } from '@/hooks/useDiscovery'
import { discoverySteps } from '@/lib/discovery-data'

import { UseCaseStep } from './steps/UseCaseStep'
import { MissionProfileStep } from './steps/MissionProfileStep'
import { ChallengeStep } from './steps/ChallengeStep'
import { ValueStep } from './steps/ValueStep'
import { SolutionStep } from './steps/SolutionStep'
import { SummaryStep } from './steps/SummaryStep'

// Context for sharing discovery state with step components
const DiscoveryContext = createContext<UseDiscoveryReturn | null>(null)

export function useDiscoveryContext() {
  const context = useContext(DiscoveryContext)
  if (!context) {
    throw new Error('useDiscoveryContext must be used within DiscoveryWizard')
  }
  return context
}

export function DiscoveryWizard() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin()
  const discovery = useDiscovery()
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward')
  const [isTransitioning, setIsTransitioning] = useState(false)

  const handleNext = useCallback(() => {
    if (!discovery.canProgress || discovery.isLastStep) return
    setDirection('forward')
    setIsTransitioning(true)
    setTimeout(() => {
      discovery.nextStep()
      setIsTransitioning(false)
    }, 200)
  }, [discovery])

  const handlePrev = useCallback(() => {
    if (discovery.isFirstStep) return
    setDirection('backward')
    setIsTransitioning(true)
    setTimeout(() => {
      discovery.prevStep()
      setIsTransitioning(false)
    }, 200)
  }, [discovery])

  const handleReset = useCallback(() => {
    setDirection('backward')
    setIsTransitioning(true)
    setTimeout(() => {
      discovery.reset()
      setIsTransitioning(false)
    }, 200)
  }, [discovery])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle if in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return
      }

      if (e.key === 'Enter' && discovery.canProgress && !discovery.isLastStep) {
        e.preventDefault()
        handleNext()
      } else if (e.key === 'Escape' && !discovery.isFirstStep) {
        e.preventDefault()
        handlePrev()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [discovery.canProgress, discovery.isFirstStep, discovery.isLastStep, handleNext, handlePrev])

  // Loading state
  if (isAdminLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Access denied
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">Access denied</p>
          <p className="text-xs text-muted-foreground/50 mt-1">
            You don't have permission to view this page
          </p>
        </div>
      </div>
    )
  }

  const currentStep = discoverySteps[discovery.currentStepIndex]

  return (
    <DiscoveryContext.Provider value={discovery}>
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex-shrink-0 px-6 py-4 border-b border-border/20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/admin"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
              </Link>
              <div>
                <h1 className="text-sm font-medium text-foreground">
                  Cyclops Value Discovery
                </h1>
                <p className="text-xs text-muted-foreground/70">
                  {currentStep.phase} · Step {discovery.currentStepIndex + 1} of{' '}
                  {discovery.totalSteps}
                </p>
              </div>
            </div>

            {!discovery.isFirstStep && (
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                Start over
              </button>
            )}
          </div>
        </header>

        {/* Progress bar */}
        <div className="flex-shrink-0 h-0.5 bg-secondary">
          <div
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${discovery.progress}%` }}
          />
        </div>

        {/* Step content */}
        <main className="flex-1 overflow-hidden">
          <div
            className={cn(
              'h-full transition-all duration-200 ease-out',
              isTransitioning && direction === 'forward' && 'wizard-exit-left',
              isTransitioning && direction === 'backward' && 'wizard-exit-right',
              !isTransitioning && 'wizard-enter'
            )}
          >
            <StepRenderer step={discovery.state.currentStep} />
          </div>
        </main>

        {/* Footer navigation */}
        <footer className="flex-shrink-0 px-6 py-4 border-t border-border/20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              {!discovery.isFirstStep && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              {/* Step indicators */}
              <div className="hidden sm:flex items-center gap-1 mr-4">
                {discoverySteps.map((step, index) => (
                  <div
                    key={step.id}
                    className={cn(
                      'w-2 h-2 rounded-full transition-all',
                      index < discovery.currentStepIndex && 'bg-primary',
                      index === discovery.currentStepIndex && 'bg-primary w-4',
                      index > discovery.currentStepIndex && 'bg-border'
                    )}
                  />
                ))}
              </div>

              {/* Continue button */}
              {!discovery.isLastStep && (
                <button
                  onClick={handleNext}
                  disabled={!discovery.canProgress}
                  className={cn(
                    'px-6 py-2 rounded-md text-sm font-medium transition-all',
                    'bg-primary text-primary-foreground',
                    'hover:bg-primary/90',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    discovery.canProgress && 'wizard-pulse'
                  )}
                >
                  Continue
                  <span className="hidden sm:inline text-primary-foreground/60 ml-2 text-xs">
                    ↵
                  </span>
                </button>
              )}
            </div>
          </div>
        </footer>

        {/* Keyboard hint */}
        <div className="hidden md:block fixed bottom-20 right-6 text-[10px] text-muted-foreground/40">
          <span className="px-1.5 py-0.5 bg-secondary/50 rounded">Enter</span> to continue ·{' '}
          <span className="px-1.5 py-0.5 bg-secondary/50 rounded">Esc</span> to go back
        </div>
      </div>
    </DiscoveryContext.Provider>
  )
}

function StepRenderer({ step }: { step: string }) {
  switch (step) {
    case 'use-case':
      return <UseCaseStep />
    case 'mission-profile':
      return <MissionProfileStep />
    case 'challenge':
      return <ChallengeStep />
    case 'value':
      return <ValueStep />
    case 'solution':
      return <SolutionStep />
    case 'summary':
      return <SummaryStep />
    default:
      return null
  }
}

